import type { DbColumn, DbTable, ViewSummary } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { AlertTriangle, Bot, LayoutDashboard, Plus, Sparkles } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { Page, PageHeader } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ViewHost, viewKeys } from '@/components/views/view-host';
import { api } from '@/lib/api';
import { useApiMutation, useDesks, useRuntimeProfiles } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { defaultRuntime, runtimeHint } from '@/routes/start';
import { Textarea } from '@/components/ui/textarea';

const NONE = '__none';

/** A first view for a table, so a person can start without writing code. */
export function starterCode(table: DbTable, columns: DbColumn[]): string {
  const name = table.schema === 'public' ? table.name : `${table.schema}.${table.name}`;
  const time = columns.find((c) => /timestamp|date/.test(c.type));
  const label = columns.find((c) => /text|char/.test(c.type) && !c.primaryKey);
  const group = columns.find((c) => /text|char|bool/.test(c.type) && c !== label && !c.primaryKey);
  const order = time ? ` ORDER BY ${time.name} DESC` : '';
  const lines = [
    `export const queries = {`,
    `  rows: "SELECT * FROM ${name}${order} LIMIT 200",`,
    `  total: "SELECT count(*)::int AS n FROM ${name}",`,
    `};`,
    ``,
    `export default function View({ data }) {`,
    `  const total = data.total[0]?.n ?? 0;`,
  ];
  if (group) lines.push(`  const byGroup = kit.count(data.rows, '${group.name}');`);
  if (time) lines.push(`  const perDay = kit.sortBy(kit.count(data.rows, (r) => kit.day(r.${time.name})), 'key');`);
  lines.push(
    `  return (`,
    `    <Stack>`,
    `      <Grid columns={${group ? 2 : 1}}>`,
    `        <Metric label="${table.name.replace(/_/g, ' ')}" value={total} hint="rows in total" />`,
  );
  if (group) lines.push(`        <Card title="By ${group.name.replace(/_/g, ' ')}"><BarChart data={byGroup} x="key" y="count" height={120} /></Card>`);
  lines.push(`      </Grid>`);
  if (time) lines.push(`      <Card title="Per day"><AreaChart data={perDay} x="key" y="count" height={140} /></Card>`);
  lines.push(`      <Table data={data.rows.slice(0, 20)} />`, `    </Stack>`, `  );`, `}`);
  return lines.join('\n');
}

const EMPTY = `export const queries = {
  now: "SELECT now() AS now, current_database() AS database",
};

export default function View({ data }) {
  const row = data.now[0];
  return (
    <Card title="Hello from the workspace database" subtitle={row.database}>
      <Text>It is {kit.formatDate(row.now)}. Edit this code to show your own tables.</Text>
    </Card>
  );
}`;

function NewViewDialog({ workspaceId, deskId, children }: { workspaceId: string; deskId?: string; children: ReactNode }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'describe' | 'table'>('describe');
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [table, setTable] = useState<string>(NONE);
  const [runtimeId, setRuntimeId] = useState<string | null>(null);
  const profiles = useRuntimeProfiles(workspaceId);
  const usable = (profiles.data ?? []).filter((p) => p.status === 'active' && p.implemented);
  const runtime = usable.find((p) => p.id === runtimeId) ?? defaultRuntime(profiles.data ?? []);
  const tables = useQuery({ queryKey: ['ws', workspaceId, 'database', 'tables'], queryFn: () => api.database.tables(workspaceId), enabled: open });
  const create = useApiMutation(
    async () => {
      if (mode === 'describe') {
        const words = prompt.trim().split(/\s+/).slice(0, 6).join(' ');
        return api.views.create(workspaceId, { title: title.trim() || words.replace(/[.,;:]+$/, '') || 'New view', deskId: deskId ?? null, prompt: prompt.trim(), runtimeProfileId: runtime!.id });
      }
      let code = EMPTY;
      const picked = tables.data?.find((x) => `${x.schema}.${x.name}` === table);
      if (picked) code = starterCode(picked, (await api.database.table(workspaceId, picked.schema, picked.name, { limit: 1 })).columns);
      return api.views.create(workspaceId, { title: title.trim() || picked?.name.replace(/_/g, ' ') || 'New view', deskId: deskId ?? null, code, note: picked ? `Started from ${picked.name}` : 'Started empty' });
    },
    {
      invalidate: [viewKeys.all(workspaceId)],
      success: mode === 'describe' ? t('View created – the view assistant is writing it') : t('View created'),
      onSuccess: (v) => {
        setOpen(false);
        setPrompt('');
        setTitle('');
        void navigate({ to: '/w/$workspaceId/views/$viewId', params: { workspaceId, viewId: v.id } });
      },
    },
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('New view')}</DialogTitle>
          <DialogDescription>{t('Describe what you want to see, and the view assistant writes the view. You can change the prompt later.')}</DialogDescription>
        </DialogHeader>
        <div className="inline-flex w-fit rounded-md border p-0.5">
          <Button size="xs" variant={mode === 'describe' ? 'secondary' : 'ghost'} onClick={() => setMode('describe')}>
            <Sparkles /> {t('Describe it')}
          </Button>
          <Button size="xs" variant={mode === 'table' ? 'secondary' : 'ghost'} onClick={() => setMode('table')}>
            {t('From a table')}
          </Button>
        </div>
        <div className="grid gap-3">
          {mode === 'describe' ? (
            <>
              <Field label={t('What do you want to see?')}>
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={5}
                  autoFocus
                  placeholder={t('e.g. Open cases as a board by status, the cases older than 48 hours as a red number, and new cases per day.')}
                />
              </Field>
              <Field label={t('Model')} hint={usable.length ? undefined : t('Connect a model under Settings → Runtimes first.')}>
                <Select value={runtime?.id} onValueChange={setRuntimeId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('Choose a runtime')} />
                  </SelectTrigger>
                  <SelectContent>
                    {usable.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                        {runtimeHint(p)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </>
          ) : (
            <Field label={t('Start from')} hint={t('a table of the workspace database')}>
              <Select value={table} onValueChange={setTable}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Choose a table')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('An empty example')}</SelectItem>
                  {(tables.data ?? []).map((tbl) => (
                    <SelectItem key={`${tbl.schema}.${tbl.name}`} value={`${tbl.schema}.${tbl.name}`}>
                      {tbl.schema === 'public' ? tbl.name : `${tbl.schema}.${tbl.name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label={t('Title')} optional>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('e.g. Open cases')} />
          </Field>
        </div>
        <DialogFooter>
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={mode === 'describe' && (!prompt.trim() || !runtime)}>
            <Sparkles /> {t('Create view')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One view as a card with its live picture (desk pages and the views list). */
export function ViewCard({ workspaceId, view }: { workspaceId: string; view: ViewSummary }) {
  const detail = useQuery({ queryKey: viewKeys.detail(view.id), queryFn: () => api.views.get(view.id) });
  return (
    <Card className="overflow-hidden">
      <Link to="/w/$workspaceId/views/$viewId" params={{ workspaceId, viewId: view.id }} className="flex items-center gap-2 border-b px-4 py-2.5 hover:bg-muted/40">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{view.title}</span>
        {view.status === 'broken' && (
          <Badge tone="amber">
            <AlertTriangle /> {t('needs repair')}
          </Badge>
        )}
        {view.proposedVersion && <Badge tone="accent">{t('proposal')}</Badge>}
        {view.createdBy.type === 'agent' && (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <Bot className="size-3" /> {view.createdBy.name ?? t('agent')}
          </span>
        )}
      </Link>
      <div className="p-4">
        {detail.data?.version ? (
          <ViewHost workspaceId={workspaceId} viewId={view.id} code={detail.data.version.code} version={detail.data.currentVersion} tables={detail.data.version.tables} />
        ) : (
          <LoadingRows rows={2} />
        )}
      </div>
    </Card>
  );
}

export function ViewsPage() {
  const { workspaceId } = useParams({ from: '/_authed/w/$workspaceId/views' });
  const views = useQuery({ queryKey: viewKeys.list(workspaceId), queryFn: () => api.views.list(workspaceId) });
  const desks = useDesks(workspaceId);
  const deskName = (id: string | null) => desks.data?.find((d) => d.id === id)?.name;
  return (
    <Page wide>
      <PageHeader
        title={t('Views')}
        description={t('Live pictures of the workspace database. They update when data comes in, and agents can build and improve them.')}
        actions={
          <NewViewDialog workspaceId={workspaceId}>
            <Button>
              <Plus /> {t('New view')}
            </Button>
          </NewViewDialog>
        }
      />
      {views.isPending ? (
        <LoadingRows rows={3} />
      ) : views.isError ? (
        <ErrorState error={views.error} onRetry={() => void views.refetch()} />
      ) : views.data.length === 0 ? (
        <EmptyState
          icon={<LayoutDashboard />}
          title={t('No views yet')}
          description={t('Create one from a table of the workspace database, or ask an agent to build one.')}
          action={
            <NewViewDialog workspaceId={workspaceId}>
              <Button>
                <Plus /> {t('New view')}
              </Button>
            </NewViewDialog>
          }
        />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {views.data.map((v) => (
            <div key={v.id} className="min-w-0">
              <ViewCard workspaceId={workspaceId} view={v} />
              <div className="mt-1 flex gap-3 px-1 text-[11px] text-muted-foreground">
                {v.deskId && <span>{t('on {desk}', { desk: deskName(v.deskId) ?? v.deskName ?? '' })}</span>}
                <span>
                  {t('version {n}', { n: v.currentVersion ?? '' })} · <RelativeTime value={v.updatedAt} />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

export { NewViewDialog };
